// Document review: the list on the left, the document itself on the right.
//
// In the drawer a document was a one-line row with a Preview toggle that
// expanded a 420px-tall frame inside a 400px-wide column. Nobody can read a
// passport or a registry extract like that, and "approve or reject each one"
// is not a reasonable instruction when the file cannot be seen. Here the
// selected document fills the pane, and the disposition sits under it.

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { useAuth } from '../../../auth/useAuth.js';
import { BulkDocumentUpload } from '../../../components/BulkDocumentUpload.js';

import { onboardingApi, type DocumentReview } from '../../../api/onboarding.api.js';

import { collectDocuments, type DocRef } from './shared.js';
import { APP_LOCALE } from '../../../utils/locale.js';

interface Props {
  readonly applicationId: string;
  /** Whose vault an agent's upload lands in. */
  readonly customerId: string;
  /** Personal files take a different slot from company paperwork. */
  readonly isBusiness: boolean;
  readonly snapshot: Record<string, unknown>;
  readonly reviews: ReadonlyMap<string, DocumentReview>;
  readonly canDecide: boolean;
  readonly onDecide: (
    docId: string,
    decision: 'VERIFY' | 'REJECT',
    note?: string,
  ) => Promise<void>;
  /** Refresh the list after staff filed documents. */
  readonly onUploaded?: () => void;
  /** Take a document off the file, or rename it. Absent means not offered here. */
  readonly onRemove?: (docId: string, kind: string, reason: string) => Promise<void>;
  readonly onRename?: (docId: string, name: string) => Promise<void>;
  /** Optional dates. Absent means the page does not offer dating at all. */
  readonly onRecordDates?: (
    docId: string,
    dates: { issuedAt: string | null; expiresAt: string | null },
  ) => Promise<void>;
}

type Filter = 'ALL' | 'PENDING' | 'VERIFIED' | 'REJECTED';

/**
 * The one date that ages a document, by the slot it was filed in.
 *
 * An identity document dies on its printed expiry; proofs and company extracts
 * go stale some months after issue. Asking for the date that matters, and only
 * that one, keeps this a one-field job rather than a form.
 */
function agingDateFor(slot: string | undefined): 'expiresAt' | 'issuedAt' {
  return slot === 'address-proof' || slot === 'trading-proof' || slot === 'director-proof' || slot === 'company'
    ? 'issuedAt'
    : 'expiresAt';
}

export function DocumentReviewSection({
  applicationId,
  customerId,
  isBusiness,
  snapshot,
  reviews,
  canDecide,
  onDecide,
  onRecordDates,
  onRemove,
  onRename,
  onUploaded,
}: Props): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [removing, setRemoving] = useState(false);
  const [removeKind, setRemoveKind] = useState('DUPLICATE');
  const [removeReason, setRemoveReason] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('ALL');
  const [url, setUrl] = useState<string | null>(null);
  const [contentType, setContentType] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [dateDraft, setDateDraft] = useState('');
  const [datesSaved, setDatesSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [decodeFailed, setDecodeFailed] = useState(false);

  // The vault decides what must be reviewed; the snapshot only supplies the
  // human labels. A file uploaded and then replaced stays in the vault while
  // the snapshot forgets it, and those rows still block the approval.
  const documents: DocRef[] = useMemo(() => {
    const fromSnapshot = collectDocuments(snapshot);
    const referenced = new Set(fromSnapshot.map((doc) => doc.id).filter((id) => id !== ''));
    const unreferenced: DocRef[] = [...reviews.values()]
      .filter((stored) => !referenced.has(stored.id))
      .map((stored) => ({
        id: stored.id,
        // A file the identity check captured is evidence, not paperwork
        // awaiting a decision — so it is labelled by where it came from rather
        // than as something superseded. It used to be hidden here and
        // mentioned in a banner pointing at another tab, which asked a reviewer
        // assembling a file to hold two lists in their head. One place for
        // everything on the customer, with each item saying what it is.
        label:
          stored.source === 'PROVIDER_CHECK'
            ? 'Captured by the identity check — approved by the provider'
            : stored.source === 'STAFF_UPLOAD'
              ? 'Filed by an agent'
            : 'Superseded — not referenced by the current application',
        name: stored.filename,
        sizeLabel: `${String(Math.max(Math.round(stored.sizeBytes / 1024), 1))} KB`,
        status: stored.status,
      }));
    // The stored name wins. A snapshot-referenced file is labelled from the
    // application, which does not know about a later rename -- so without this
    // a renamed document kept its old name exactly where it is reviewed.
    const current = fromSnapshot.map((doc) => {
      const stored = reviews.get(doc.id);
      return stored === undefined ? doc : { ...doc, name: stored.filename };
    });
    return [...current, ...unreferenced];
  }, [snapshot, reviews]);

  const statusOf = (doc: DocRef): string => reviews.get(doc.id)?.status ?? doc.status;
  const visible = documents.filter((doc) => filter === 'ALL' || statusOf(doc) === filter);
  const selected = documents.find((doc) => doc.id === selectedId) ?? null;
  const selectedReview = selected === null ? undefined : reviews.get(selected.id);

  // A date typed for one file must never be saved against the next one the
  // reviewer clicks on.
  const shownId = selected?.id ?? '';
  useEffect(() => {
    setDateDraft('');
    setDatesSaved(false);
  }, [shownId]);

  // Load the bytes whenever the selection changes, and revoke the previous
  // object URL — a reviewer clicking through twelve documents must not leak
  // twelve blobs.
  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;
    setDecodeFailed(false);
    setError('');
    setNote('');
    if (selected === null || selected.id === '') {
      setUrl(null);
      return () => undefined;
    }
    setLoading(true);
    void (async () => {
      try {
        const blob = await onboardingApi.fetchDocumentBlob(applicationId, selected.id);
        if (cancelled) return;
        created = URL.createObjectURL(blob);
        setUrl(created);
        setContentType(blob.type);
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load document');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (created !== null) URL.revokeObjectURL(created);
    };
  }, [applicationId, selected]);

  const decide = async (decision: 'VERIFY' | 'REJECT'): Promise<void> => {
    if (selected === null) return;
    if (decision === 'REJECT' && note.trim().length < 4) {
      setError('A rejection needs a reason of at least 4 characters.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await onDecide(selected.id, decision, note.trim() === '' ? undefined : note.trim());
      setNote('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not record the decision');
    } finally {
      setBusy(false);
    }
  };

  const download = (): void => {
    if (url === null || selected === null) return;
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = selected.name;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  const counts = {
    PENDING: documents.filter((doc) => statusOf(doc) === 'PENDING').length,
    VERIFIED: documents.filter((doc) => statusOf(doc) === 'VERIFIED').length,
    REJECTED: documents.filter((doc) => statusOf(doc) === 'REJECTED').length,
  };

  const heldForParties = [...reviews.values()].filter(
    (stored) => stored.source === 'PROVIDER_CHECK',
  );

  if (documents.length === 0) {
    // An empty file is exactly when staff need to add to it, so the upload
    // stays offered here.
    return (
      <Stack spacing={1.5}>
        <Alert severity="info">No documents are attached to this application.</Alert>
        {canDecide ? (
          <Box>
            <BulkDocumentUpload customerId={customerId} isBusiness={isBusiness} onUploaded={() => onUploaded?.()} />
          </Box>
        ) : null}
      </Stack>
    );
  }

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
      <Box sx={{ width: { xs: '100%', md: 340 }, flexShrink: 0 }}>
        <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: 'wrap' }}>
          {(['ALL', 'PENDING', 'VERIFIED', 'REJECTED'] as Filter[]).map((option) => (
            <Chip
              key={option}
              size="small"
              label={
                option === 'ALL'
                  ? `All ${String(documents.length)}`
                  : `${option.charAt(0)}${option.slice(1).toLowerCase()} ${String(counts[option])}`
              }
              color={filter === option ? 'primary' : 'default'}
              onClick={() => setFilter(option)}
            />
          ))}
        </Stack>
        <Box sx={{ maxHeight: 620, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          {visible.map((doc, index) => {
            const status = statusOf(doc);
            return (
              <Box
                key={`${doc.id || doc.name}-${String(index)}`}
                onClick={() => setSelectedId(doc.id)}
                sx={{
                  p: 1,
                  cursor: doc.id === '' ? 'default' : 'pointer',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: doc.id === selectedId ? 'action.selected' : undefined,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" title={doc.name} sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    {doc.name}
                  </Typography>
                  {reviews.get(doc.id)?.removedAt ? (
                    <Chip size="small" sx={{ flexShrink: 0 }} label="REMOVED" variant="outlined" color="warning" />
                  ) : null}
                  <Chip
                    size="small"
                    sx={{ flexShrink: 0 }}
                    label={status}
                    color={
                      status === 'VERIFIED' ? 'success' : status === 'REJECTED' ? 'error' : 'default'
                    }
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {doc.label}
                  {doc.sizeLabel !== '' ? ` · ${doc.sizeLabel}` : ''}
                </Typography>
              </Box>
            );
          })}
          {visible.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
              Nothing in this state.
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {canDecide ? (
          <Box sx={{ mb: 1.5 }}>
            {/* Paperwork arrives by email, by phone, in a branch; staff file
                it here, into the same vault and the same review flow. */}
            <BulkDocumentUpload customerId={customerId} isBusiness={isBusiness} onUploaded={() => onUploaded?.()} />
          </Box>
        ) : null}

        {heldForParties.length > 0 ? (
          <Alert severity="info" sx={{ mb: 1 }}>
            {String(heldForParties.length)} file(s) below were captured by the identity check and
            approved by the provider. They are evidence of a check that passed rather than
            paperwork awaiting your decision — listed here so the whole file is in one place.
          </Alert>
        ) : null}
        {selected === null ? (
          <Alert severity="info">Select a document to review it.</Alert>
        ) : (
          <>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ flexGrow: 1 }} noWrap title={selected.name}>
                {selected.name}
              </Typography>
              <Button size="small" disabled={url === null} onClick={download}>
                Download
              </Button>
              {canDecide && onRename !== undefined && selectedReview !== undefined && !selectedReview.removedAt ? (
                <Button
                  size="small"
                  onClick={() => {
                    setNewName(selected.name);
                    setRenaming(true);
                  }}
                >
                  Rename
                </Button>
              ) : null}
              {canDecide && onRemove !== undefined && selectedReview !== undefined && !selectedReview.removedAt ? (
                <Button
                  size="small"
                  color="error"
                  onClick={() => {
                    setRemoveKind('DUPLICATE');
                    setRemoveReason('');
                    setRemoving(true);
                  }}
                >
                  Remove
                </Button>
              ) : null}
            </Stack>
            {selectedReview?.originalFilename ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Uploaded as {selectedReview.originalFilename}
              </Typography>
            ) : null}
            {selectedReview?.removedAt ? (
              <Alert severity="warning" sx={{ mb: 1 }}>
                Removed from the file {new Date(selectedReview.removedAt).toLocaleString(APP_LOCALE)}
                {selectedReview.removeReason ? ` — ${selectedReview.removeReason}` : ''}. Shown to
                administrators only; it no longer counts towards the application.
              </Alert>
            ) : null}

            <Dialog open={renaming} onClose={() => setRenaming(false)} fullWidth maxWidth="sm">
              <DialogTitle>Rename this document</DialogTitle>
              <DialogContent>
                <TextField
                  autoFocus
                  fullWidth
                  sx={{ mt: 1 }}
                  label="Name"
                  value={newName}
                  onChange={(event) => setNewName(event.target.value)}
                  inputProps={{ maxLength: 200 }}
                  helperText="The name it was uploaded under is kept in the history."
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRenaming(false)} disabled={busy}>Cancel</Button>
                <Button
                  variant="contained"
                  disabled={busy || newName.trim().length < 2 || newName.trim() === selected.name}
                  onClick={() => {
                    if (onRename === undefined) return;
                    setBusy(true);
                    setError('');
                    onRename(selected.id, newName.trim())
                      .then(() => setRenaming(false))
                      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not rename the document'))
                      .finally(() => setBusy(false));
                  }}
                >
                  Rename
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog open={removing} onClose={() => setRemoving(false)} fullWidth maxWidth="sm">
              <DialogTitle>Remove this document</DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                  <Typography variant="body2">
                    It leaves the working lists and stops counting towards the application. The file
                    itself is kept, and the removal is recorded on the client&rsquo;s file with your
                    name and reason.
                  </Typography>
                  <TextField
                    select
                    label="What is wrong with it"
                    value={removeKind}
                    onChange={(event) => setRemoveKind(event.target.value)}
                  >
                    <MenuItem value="DUPLICATE">A duplicate</MenuItem>
                    <MenuItem value="WRONG_TYPE">The wrong kind of document</MenuItem>
                    {/* Somebody else's paperwork on this file: an administrator's call. */}
                    {isAdmin ? <MenuItem value="WRONG_CLIENT">Filed against the wrong client</MenuItem> : null}
                    <MenuItem value="OTHER">Something else</MenuItem>
                  </TextField>
                  <TextField
                    label="Why"
                    value={removeReason}
                    onChange={(event) => setRemoveReason(event.target.value)}
                    multiline
                    minRows={2}
                    inputProps={{ maxLength: 1000 }}
                    required
                  />
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRemoving(false)} disabled={busy}>Cancel</Button>
                <Button
                  color="error"
                  variant="contained"
                  disabled={busy || removeReason.trim().length < 4}
                  onClick={() => {
                    if (onRemove === undefined) return;
                    setBusy(true);
                    setError('');
                    onRemove(selected.id, removeKind, removeReason.trim())
                      .then(() => {
                        setRemoving(false);
                        setSelectedId(null);
                      })
                      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not remove the document'))
                      .finally(() => setBusy(false));
                  }}
                >
                  Remove
                </Button>
              </DialogActions>
            </Dialog>

            {error !== '' ? (
              <Alert severity="error" sx={{ mb: 1 }}>
                {error}
              </Alert>
            ) : null}

            {selected.id === '' ? (
              <Alert severity="warning">
                Captured before the document vault — there are no stored bytes to review. Ask the
                applicant to re-upload it.
              </Alert>
            ) : (
              <Box
                sx={{
                  height: 620,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: 'background.default',
                }}
              >
                {loading ? (
                  <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                    <Typography variant="body2" color="text.secondary">
                      Loading…
                    </Typography>
                  </Stack>
                ) : url === null ? null : decodeFailed ? (
                  <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', p: 2 }}>
                    <Typography variant="body2" color="text.secondary" align="center">
                      This file could not be rendered as an image — its contents do not match its
                      type. Download it to inspect the actual bytes.
                    </Typography>
                  </Stack>
                ) : contentType.startsWith('image/') ? (
                  <Box
                    component="img"
                    src={url}
                    alt={selected.name}
                    onError={() => setDecodeFailed(true)}
                    sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  <Box
                    component="iframe"
                    src={url}
                    title={selected.name}
                    sx={{ width: '100%', height: '100%', border: 0 }}
                  />
                )}
              </Box>
            )}

            {selectedReview?.reviewedAt !== null && selectedReview?.reviewedAt !== undefined ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {selectedReview.status === 'VERIFIED' ? 'Approved' : 'Rejected'} by{' '}
                {selectedReview.reviewedBy ?? '—'} ·{' '}
                {new Date(selectedReview.reviewedAt).toLocaleString(APP_LOCALE)}
                {selectedReview.reviewNote !== null && selectedReview.reviewNote !== ''
                  ? ` · ${selectedReview.reviewNote}`
                  : ''}
              </Typography>
            ) : null}

            {canDecide && selected.id !== '' ? (
              <>
                <Divider sx={{ my: 1.5 }} />
                {/* These decide THIS FILE, not the application. The page also
                    carries an Approve at the top that activates the customer,
                    and two identically-worded green buttons on one screen is
                    an invitation to click the wrong one — so this pair names
                    its scope and stays a secondary action. */}
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Decision for this document only — <b>{selected.name}</b>
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    fullWidth
                    label="Note (required to reject)"
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                  />
                  <Button
                    variant="outlined"
                    color="success"
                    disabled={busy}
                    onClick={() => void decide('VERIFY')}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Approve document
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={busy}
                    onClick={() => void decide('REJECT')}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Reject document
                  </Button>
                </Stack>

                {onRecordDates !== undefined && selectedReview !== undefined ? (
                  <DocumentDateField
                    review={selectedReview}
                    draft={dateDraft}
                    saved={datesSaved}
                    busy={busy}
                    onChange={(value) => {
                      setDateDraft(value);
                      setDatesSaved(false);
                    }}
                    onSave={async (value) => {
                      setBusy(true);
                      setError('');
                      try {
                        const field = agingDateFor(selectedReview.slot);
                        await onRecordDates(selected.id, {
                          issuedAt: field === 'issuedAt' ? value : (selectedReview.issuedAt ?? null),
                          expiresAt: field === 'expiresAt' ? value : (selectedReview.expiresAt ?? null),
                        });
                        setDatesSaved(true);
                      } catch (err: unknown) {
                        setError(err instanceof Error ? err.message : 'Could not record the date');
                      } finally {
                        setBusy(false);
                      }
                    }}
                  />
                ) : null}
              </>
            ) : null}
          </>
        )}
      </Box>
    </Stack>
  );
}

/**
 * An optional date on the document being looked at.
 *
 * Offered, never required: approving a document does not depend on it, and a
 * document left undated raises no alarm anywhere. Where a reviewer does record
 * one, the client header will flag it once it lapses.
 */
function DocumentDateField({
  review,
  draft,
  saved,
  busy,
  onChange,
  onSave,
}: {
  review: DocumentReview;
  draft: string;
  saved: boolean;
  busy: boolean;
  onChange: (value: string) => void;
  onSave: (value: string | null) => Promise<void>;
}): JSX.Element {
  const field = agingDateFor(review.slot);
  const current = (field === 'issuedAt' ? review.issuedAt : review.expiresAt) ?? null;
  const value = draft !== '' ? draft : (current ?? '');
  const label = field === 'issuedAt' ? 'Date of issue (optional)' : 'Expiry date (optional)';

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
      <TextField
        size="small"
        type="date"
        label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        InputLabelProps={{ shrink: true }}
        helperText={
          field === 'issuedAt'
            ? 'Read it off the document. It goes stale some months after this.'
            : 'Read it off the document. It lapses on this date.'
        }
      />
      <Button
        size="small"
        disabled={busy || value === (current ?? '')}
        onClick={() => void onSave(value === '' ? null : value)}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {value === '' && current !== null ? 'Clear date' : 'Save date'}
      </Button>
      {saved ? (
        <Typography variant="caption" color="success.main">
          Saved
        </Typography>
      ) : null}
    </Stack>
  );
}
